import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Package, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Login() {
    const { user, login, verify2FA, request2FAEmail } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    // 2FA state — populated after the password step when the user has 2FA on
    const [stage, setStage] = useState("password");           // "password" | "2fa"
    const [twoFa, setTwoFa] = useState(null);                  // {methods, pending_token}
    const [method2FA, setMethod2FA] = useState("totp");
    const [code, setCode] = useState("");
    const [emailSent, setEmailSent] = useState(false);

    if (user) return <Navigate to="/" replace />;

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        const res = await login(email, password);
        setBusy(false);
        if (!res.ok) {
            toast.error(res.error);
            return;
        }
        if (res.requires_2fa) {
            setMethod2FA(res.methods.includes("totp") ? "totp" : "email");
            setTwoFa({ methods: res.methods, pending_token: res.pending_token });
            setStage("2fa");
            return;
        }
        navigate("/");
    };

    const sendEmailCode = async () => {
        const r = await request2FAEmail(twoFa.pending_token);
        if (r.ok) {
            toast.success("Code envoyé sur votre adresse email.");
            setEmailSent(true);
        } else {
            toast.error(r.error);
        }
    };

    const submitCode = async (e) => {
        e.preventDefault();
        setBusy(true);
        const r = await verify2FA({ pending_token: twoFa.pending_token, method: method2FA, code });
        setBusy(false);
        if (!r.ok) {
            toast.error(r.error);
            return;
        }
        navigate("/");
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-background">
            <div className="hidden lg:flex flex-col justify-between p-12 bg-secondary text-secondary-foreground relative overflow-hidden">
                <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-md bg-primary flex items-center justify-center">
                        <Package weight="duotone" size={20} />
                    </div>
                    <div className="font-display text-xl font-bold">ScanBox</div>
                </div>
                <div className="space-y-4 max-w-md">
                    <p className="overline text-primary/80">Vos factures, automatisées</p>
                    <h1 className="font-display text-4xl lg:text-5xl font-bold leading-tight tracking-tight">
                        Récupérez, classez et analysez vos factures en un clic.
                    </h1>
                    <p className="text-secondary-foreground/70 leading-relaxed text-base">
                        Synchronisation automatique, détection des factures manquantes, suivi mensuel des dépenses
                        et impression groupée.
                    </p>
                </div>
                <div className="text-xs text-secondary-foreground/50 font-mono">
                    v1.0 — préversion par NetKom Informatique 2026
                </div>
            </div>

            <div className="flex items-center justify-center p-8">
                <Card className="w-full max-w-md border-border">
                    <CardContent className="p-8 space-y-6">
                        <div>
                            <p className="overline">Connexion</p>
                            <h2 className="font-display text-3xl font-bold mt-2 tracking-tight">Bienvenue</h2>
                            <p className="text-muted-foreground text-sm mt-2">
                                Connectez-vous pour accéder à votre tableau de bord.
                            </p>
                        </div>

                        <form className="space-y-4" onSubmit={stage === "2fa" ? submitCode : submit}>
                            {stage === "password" && (
                                <>
                                    <div className="space-y-2">
                                        <Label htmlFor="email">Email</Label>
                                        <Input
                                            id="email"
                                            type="email"
                                            required
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="vous@exemple.com"
                                            data-testid="login-email"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="password">Mot de passe</Label>
                                        <Input
                                            id="password"
                                            type="password"
                                            required
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            data-testid="login-password"
                                        />
                                    </div>
                                </>
                            )}
                            {stage === "2fa" && (
                                <div className="space-y-3" data-testid="login-2fa-stage">
                                    <p className="text-sm text-muted-foreground">
                                        Vérification en deux étapes — choisissez votre méthode :
                                    </p>
                                    {twoFa.methods.length > 1 && (
                                        <div className="flex gap-2">
                                            {twoFa.methods.includes("totp") && (
                                                <button
                                                    type="button"
                                                    onClick={() => setMethod2FA("totp")}
                                                    className={`flex-1 py-2 px-3 rounded-md border text-sm font-semibold transition ${method2FA === "totp" ? "border-primary bg-accent/30" : "border-border hover:border-primary/50"}`}
                                                    data-testid="login-2fa-method-totp"
                                                >
                                                    📱 Application
                                                </button>
                                            )}
                                            {twoFa.methods.includes("email") && (
                                                <button
                                                    type="button"
                                                    onClick={() => setMethod2FA("email")}
                                                    className={`flex-1 py-2 px-3 rounded-md border text-sm font-semibold transition ${method2FA === "email" ? "border-primary bg-accent/30" : "border-border hover:border-primary/50"}`}
                                                    data-testid="login-2fa-method-email"
                                                >
                                                    📧 Email
                                                </button>
                                            )}
                                        </div>
                                    )}
                                    {method2FA === "email" && !emailSent && (
                                        <Button type="button" variant="outline" className="w-full" onClick={sendEmailCode} data-testid="login-2fa-email-send">
                                            Envoyer un code par email
                                        </Button>
                                    )}
                                    <div className="space-y-2">
                                        <Label htmlFor="code">Code à 6 chiffres</Label>
                                        <Input
                                            id="code"
                                            type="text"
                                            inputMode="numeric"
                                            maxLength={6}
                                            value={code}
                                            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                                            placeholder="123456"
                                            className="font-mono tabular text-xl tracking-widest text-center"
                                            autoFocus
                                            data-testid="login-2fa-code"
                                        />
                                    </div>
                                </div>
                            )}
                            <Button
                                type="submit"
                                disabled={busy || (stage === "2fa" && code.length !== 6)}
                                className="w-full bg-primary hover:bg-primary/90"
                                data-testid="login-submit"
                            >
                                {busy ? "Vérification…" : (stage === "2fa" ? "Valider le code" : "Se connecter")}
                                <ArrowRight size={16} className="ml-2" />
                            </Button>
                            {stage === "2fa" && (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    className="w-full text-xs"
                                    onClick={() => { setStage("password"); setCode(""); setTwoFa(null); setEmailSent(false); }}
                                    data-testid="login-2fa-back"
                                >
                                    ← Revenir à la saisie du mot de passe
                                </Button>
                            )}
                        </form>

                        <div className="text-sm text-muted-foreground text-center">
                            Pas encore de compte ?{" "}
                            <Link to="/register" className="text-primary font-semibold hover:underline" data-testid="link-register">
                                Créer un compte
                            </Link>
                            <div className="mt-2">
                                <Link to="/forgot-password" className="text-xs text-muted-foreground hover:text-primary hover:underline" data-testid="link-forgot">
                                    Mot de passe oublié ?
                                </Link>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
